package com.daytimegaming.ourdailyfinances.presentation.auth

import androidx.compose.animation.AnimatedVisibilityScope
import androidx.compose.animation.ExperimentalSharedTransitionApi
import androidx.compose.animation.SharedTransitionScope
import androidx.compose.animation.animateContentSize
import androidx.compose.animation.core.FastOutSlowInEasing
import androidx.compose.animation.core.RepeatMode
import androidx.compose.animation.core.animateFloat
import androidx.compose.animation.core.infiniteRepeatable
import androidx.compose.animation.core.rememberInfiniteTransition
import androidx.compose.animation.core.tween
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.IntrinsicSize
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeContent
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.layout.windowInsetsPadding
import androidx.compose.foundation.layout.wrapContentHeight
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.AccountBalance
import androidx.compose.material.icons.filled.ArrowDropDown
import androidx.compose.material.icons.filled.ArrowDropUp
import androidx.compose.foundation.BorderStroke
import androidx.compose.material.icons.filled.Check
import androidx.compose.material.icons.filled.ChevronRight
import androidx.compose.material.icons.filled.CreditCard
import androidx.compose.material.icons.filled.Dashboard
import androidx.compose.material.icons.filled.Payments
import androidx.compose.material.icons.filled.PlayArrow
import androidx.compose.material.icons.filled.ShoppingCart
import androidx.compose.material.icons.filled.ShowChart
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.Divider
import androidx.compose.material3.Icon
import androidx.compose.material3.LinearProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.daytimegaming.ourdailyfinances.presentation.components.AppName
import com.daytimegaming.ourdailyfinances.presentation.components.LandingScreenBackground
import com.daytimegaming.ourdailyfinances.presentation.theme.GlassCard
import org.jetbrains.compose.resources.painterResource
import ourdailyfinances.shared.generated.resources.Res
import ourdailyfinances.shared.generated.resources.hero_laptop

@OptIn(ExperimentalSharedTransitionApi::class)
@Composable
fun LandingScreen(
    sharedTransitionScope: SharedTransitionScope,
    animatedVisibilityScope: AnimatedVisibilityScope,
    onNavigateToLogin: () -> Unit,
    onNavigateToRegister: () -> Unit,
    onNavigateToPrivacyPolicy: () -> Unit = {},
    onNavigateToTermsOfService: () -> Unit = {},
) {
    // Stitch Theme Colors
    val primaryColor = Color(0xFFD6FFF6)
    val accentColor = Color(0xFF57F1DB) // Neon Teal
    val outlineVariantColor = Color(0xFF3C4A46)
    val textSecondaryColor = Color(0xFFBACAC5)
    
    val accentGradient = Brush.horizontalGradient(
        colors = listOf(
            accentColor,
            Color(0xFF006B60)
        )
    )

    LandingScreenBackground {
        Box(
            modifier = Modifier.fillMaxSize()
        ) {
            val scrollState = rememberScrollState()

            // Main Content Area with top bar padding
            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .verticalScroll(scrollState)
                    .windowInsetsPadding(WindowInsets.safeContent)
                    .padding(top = 80.dp), // Header offset
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                BoxWithConstraints(
                    modifier = Modifier.fillMaxWidth()
                ) {
                    val isCompact = maxWidth < 850.dp
                    val horizontalPadding = if (isCompact) 20.dp else 40.dp

                    Column(
                        modifier = Modifier
                            .align(Alignment.TopCenter)
                            .widthIn(max = 1800.dp)
                            .padding(horizontal = horizontalPadding),
                        horizontalAlignment = Alignment.CenterHorizontally,
                        verticalArrangement = Arrangement.spacedBy(64.dp)
                    ) {
                        
                        // --- HERO SECTION ---
                        HeroSection(
                            isCompact = isCompact,
                            accentGradient = accentGradient,
                            accentColor = accentColor,
                            primaryColor = primaryColor,
                            textSecondaryColor = textSecondaryColor,
                            onGetStarted = onNavigateToRegister,
                            onNavigateToLogin = onNavigateToLogin
                        )

                        // --- SOPHISTICATED FINANCIAL CONTROL (BENTO GRID) ---
                        BentoGridSection(
                            isCompact = isCompact,
                            accentColor = accentColor,
                            textSecondaryColor = textSecondaryColor
                        )

                        // --- PRICING SECTION ---
                        PricingSection(
                            accentColor = accentColor,
                            textSecondaryColor = textSecondaryColor,
                            isCompact = isCompact,
                            onNavigateToRegister = {}, //onNavigateToRegister
                        )

                        // --- FAQ SECTION ---
                        FaqSection(
                            accentColor = accentColor,
                            textSecondaryColor = textSecondaryColor
                        )

                        // --- CTA SECTION ---
                        CtaSection(
                            accentGradient = accentGradient,
                            accentColor = accentColor,
                            textSecondaryColor = textSecondaryColor,
                            onGetStarted = onNavigateToRegister
                        )

                        // --- FOOTER SECTION ---
                        FooterSection(
                            primaryColor = primaryColor,
                            textSecondaryColor = textSecondaryColor,
                            onPrivacyClick = onNavigateToPrivacyPolicy,
                            onTermsClick = onNavigateToTermsOfService
                        )
                    }
                }
            }

            // --- STICKY TOP NAVBAR ---
            BoxWithConstraints(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(80.dp)
                    .background(Color(0xFF0E1513)) // Muted surface / 80% opacity
                    .border(width = 1.dp, color = Color(0x1AFFFFFF), shape = RoundedCornerShape(0.dp))
                    .align(Alignment.TopCenter)
            ) {
                val isCompact = maxWidth < 850.dp
                val horizontalPadding = if (isCompact) 20.dp else 40.dp

                Row(
                    modifier = Modifier
                        .fillMaxSize()
                        .align(Alignment.Center)
                        .widthIn(max = 1200.dp)
                        .padding(horizontal = horizontalPadding),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.SpaceBetween
                ) {
                    // Desktop menu options
                    if (!isCompact) {
                        AppName(
                            sharedTransitionScope = sharedTransitionScope,
                            animatedVisibilityScope = animatedVisibilityScope,
                        )

                        Row(
                            horizontalArrangement = Arrangement.spacedBy(24.dp),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Text(
                                text = "Features",
                                style = MaterialTheme.typography.bodyMedium.copy(color = textSecondaryColor),
                                modifier = Modifier.clickable { }
                            )
                            Text(
                                text = "Shared Dashboards",
                                style = MaterialTheme.typography.bodyMedium.copy(color = textSecondaryColor),
                                modifier = Modifier.clickable { }
                            )
                            Text(
                                text = "Security",
                                style = MaterialTheme.typography.bodyMedium.copy(color = textSecondaryColor),
                                modifier = Modifier.clickable { }
                            )
                            Text(
                                text = "Pricing",
                                style = MaterialTheme.typography.bodyMedium.copy(color = textSecondaryColor),
                                modifier = Modifier.clickable { }
                            )
                        }
                    } else {
                        AppName(
                            sharedTransitionScope = sharedTransitionScope,
                            animatedVisibilityScope = animatedVisibilityScope,
                            style = MaterialTheme.typography.headlineSmall.copy(
                                fontWeight = FontWeight.Bold,
                                letterSpacing = 1.sp
                            ),
                        )
                    }

                    // Action buttons
                    Row(
                        horizontalArrangement = Arrangement.spacedBy(12.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        OutlinedButton(
                            onClick = onNavigateToLogin,
                            shape = RoundedCornerShape(12.dp),
                            colors = ButtonDefaults.outlinedButtonColors(
                                contentColor = accentColor
                            ),
                        ) {
                            Text(
                                text = "Log In",
                                style = MaterialTheme.typography.labelLarge.copy(
                                    fontWeight = FontWeight.Bold
                                )
                            )
                        }
                        
                        Button(
                            onClick = {}, //onNavigateToRegister,
                            shape = RoundedCornerShape(12.dp),
                            colors = ButtonDefaults.buttonColors(
                                containerColor = accentColor,
                                contentColor = Color(0xFF0E1513)
                            )
                        ) {
                            Text(
                                text = "Get Started",
                                style = MaterialTheme.typography.labelLarge.copy(
                                    fontWeight = FontWeight.Bold
                                )
                            )
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun HeroSection(
    isCompact: Boolean,
    accentGradient: Brush,
    accentColor: Color,
    primaryColor: Color,
    textSecondaryColor: Color,
    onGetStarted: () -> Unit,
    onNavigateToLogin: () -> Unit
) {
    if (isCompact) {
        Column(
            horizontalAlignment = Alignment.CenterHorizontally,
            modifier = Modifier.fillMaxWidth().padding(top = 24.dp)
        ) {
            HouseholdBadge()
            Spacer(modifier = Modifier.height(24.dp))
            Text(
                text = "Master Your\nHousehold Finances\nTogether",
                style = MaterialTheme.typography.displayMedium.copy(
                    fontWeight = FontWeight.Black,
                    color = Color.White,
                    lineHeight = 44.sp,
                    textAlign = TextAlign.Center
                )
            )
            Spacer(modifier = Modifier.height(16.dp))
            Text(
                text = "The elite financial hub for modern households. Sync accounts, track goals, and build wealth with a platform designed for clarity and shared transparency.",
                style = MaterialTheme.typography.bodyLarge.copy(
                    color = textSecondaryColor,
                    lineHeight = 24.sp,
                    textAlign = TextAlign.Center
                ),
                modifier = Modifier.fillMaxWidth(0.95f)
            )
            Spacer(modifier = Modifier.height(32.dp))

            Column(
                modifier = Modifier.fillMaxWidth(0.85f),
                verticalArrangement = Arrangement.spacedBy(16.dp),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                Button(
                    onClick = {}, //onGetStarted,
                    colors = ButtonDefaults.buttonColors(containerColor = Color.Transparent),
                    contentPadding = PaddingValues(),
                    shape = RoundedCornerShape(12.dp),
                    modifier = Modifier
                        .height(52.dp)
                        .fillMaxWidth()
                        .background(accentGradient, RoundedCornerShape(12.dp))
                ) {
                    Text(
                        text = "Get Started for Free",
                        style = MaterialTheme.typography.titleMedium.copy(
                            fontWeight = FontWeight.Bold,
                            color = Color(0xFF0E1513)
                        )
                    )
                }

                GlassCard(
                    modifier = Modifier
                        .height(52.dp)
                        .fillMaxWidth(),
                    onClick = { }
                ) {
                    Row(
                        modifier = Modifier.fillMaxSize(),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.Center
                    ) {
                        Icon(
                            imageVector = Icons.Default.PlayArrow,
                            contentDescription = "Watch Demo",
                            tint = Color.White,
                            modifier = Modifier.size(20.dp)
                        )
                        Spacer(modifier = Modifier.width(8.dp))
                        Text(
                            text = "Watch the Demo",
                            style = MaterialTheme.typography.bodyMedium.copy(
                                fontWeight = FontWeight.Bold,
                                color = Color.White
                            )
                        )
                    }
                }
            }

            IntegrationReadyRow(
                textSecondaryColor = textSecondaryColor,
                alignment = Alignment.CenterHorizontally
            )
        }
    } else {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .widthIn(max = 1200.dp)
                .padding(vertical = 40.dp),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            Column(
                modifier = Modifier.weight(1.15f),
                horizontalAlignment = Alignment.Start
            ) {
                Text(
                    text = "Master Your\nHousehold Finances\nTogether",
                    style = MaterialTheme.typography.displayLarge.copy(
                        fontWeight = FontWeight.Black,
                        color = Color.White,
                        lineHeight = 62.sp
                    )
                )
                Spacer(modifier = Modifier.height(20.dp))
                Text(
                    text = "The elite financial hub for modern households. Sync accounts, track goals, and build wealth with a platform designed for clarity and shared transparency.",
                    style = MaterialTheme.typography.bodyLarge.copy(
                        color = textSecondaryColor,
                        lineHeight = 26.sp
                    ),
                    modifier = Modifier.fillMaxWidth(0.9f)
                )
                Spacer(modifier = Modifier.height(36.dp))

                Row(
                    horizontalArrangement = Arrangement.spacedBy(16.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Button(
                        onClick = {}, //onGetStarted,
                        colors = ButtonDefaults.buttonColors(containerColor = Color.Transparent),
                        contentPadding = PaddingValues(),
                        shape = RoundedCornerShape(12.dp),
                        modifier = Modifier
                            .height(52.dp)
                            .width(220.dp)
                            .background(accentGradient, RoundedCornerShape(12.dp))
                    ) {
                        Text(
                            text = "Get Started for Free",
                            style = MaterialTheme.typography.bodyLarge.copy(
                                fontWeight = FontWeight.Bold,
                                color = Color(0xFF0E1513)
                            )
                        )
                    }

                    GlassCard(
                        modifier = Modifier
                            .height(52.dp)
                            .width(200.dp),
                        onClick = { }
                    ) {
                        Row(
                            modifier = Modifier.fillMaxSize(),
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.Center
                        ) {
                            Icon(
                                imageVector = Icons.Default.PlayArrow,
                                contentDescription = "Watch Demo",
                                tint = Color.White,
                                modifier = Modifier.size(20.dp)
                            )
                            Spacer(modifier = Modifier.width(8.dp))
                            Text(
                                text = "Watch Demo",
                                style = MaterialTheme.typography.bodyMedium.copy(
                                    fontWeight = FontWeight.Bold,
                                    color = Color.White
                                )
                            )
                        }
                    }
                }

                IntegrationReadyRow(
                    textSecondaryColor = textSecondaryColor,
                    alignment = Alignment.Start
                )
            }

            Box(
                modifier = Modifier
                    .weight(.85f)
                    .wrapContentHeight(Alignment.CenterVertically),
                contentAlignment = Alignment.Center
            ) {
                HeroLaptopImage()
            }
        }
    }
}

@Composable
private fun HouseholdBadge() {
    Row(
        verticalAlignment = Alignment.CenterVertically,
        modifier = Modifier
            .border(
                width = 1.dp,
                color = Color(0x3357F1DB),
                shape = RoundedCornerShape(50)
            )
            .background(Color(0x1157F1DB), RoundedCornerShape(50))
            .padding(horizontal = 14.dp, vertical = 6.dp)
    ) {
        PulseIndicator()
        Spacer(modifier = Modifier.width(10.dp))
        Text(
            text = "TRUSTED BY 5+ HOUSEHOLDS",
            style = MaterialTheme.typography.labelSmall.copy(
                fontWeight = FontWeight.Bold,
                color = Color(0xFF57F1DB),
                letterSpacing = 1.sp
            )
        )
    }
}

@Composable
private fun PulseIndicator(modifier: Modifier = Modifier) {
    val infiniteTransition = rememberInfiniteTransition()
    val scale by infiniteTransition.animateFloat(
        initialValue = 0.8f,
        targetValue = 1.6f,
        animationSpec = infiniteRepeatable(
            animation = tween(1400, easing = FastOutSlowInEasing),
            repeatMode = RepeatMode.Reverse
        )
    )
    val alpha by infiniteTransition.animateFloat(
        initialValue = 0.6f,
        targetValue = 0.0f,
        animationSpec = infiniteRepeatable(
            animation = tween(1400, easing = FastOutSlowInEasing),
            repeatMode = RepeatMode.Reverse
        )
    )

    Box(
        modifier = modifier.size(14.dp),
        contentAlignment = Alignment.Center
    ) {
        Box(
            modifier = Modifier
                .fillMaxSize()
                .graphicsLayer(scaleX = scale, scaleY = scale, alpha = alpha)
                .clip(RoundedCornerShape(50))
                .background(Color(0xFF57F1DB))
        )
        Box(
            modifier = Modifier
                .size(7.dp)
                .clip(RoundedCornerShape(50))
                .background(Color(0xFF57F1DB))
        )
    }
}

@Composable
private fun HeroLaptopImage(modifier: Modifier = Modifier) {
    GlassCard(
        modifier = modifier
            .fillMaxWidth(.9f)
            .padding(8.dp)
    ) {
        Image(
            painter = painterResource(Res.drawable.hero_laptop),
            contentDescription = "Our Daily Finances Dashboard on Laptop",
            modifier = Modifier
                .fillMaxWidth()
                .clip(RoundedCornerShape(12.dp))
        )
    }
}

@Composable
private fun BentoGridSection(
    isCompact: Boolean,
    accentColor: Color,
    textSecondaryColor: Color
) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .widthIn(max = 1200.dp),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Text(
            text = "Sophisticated Financial Control",
            style = MaterialTheme.typography.headlineMedium.copy(
                fontWeight = FontWeight.Bold,
                color = Color.White
            ),
            textAlign = TextAlign.Center
        )
        Spacer(modifier = Modifier.height(12.dp))
        Text(
            text = "Precision tools for the modern household, built with security and collaboration at the core.",
            style = MaterialTheme.typography.bodyLarge.copy(color = textSecondaryColor),
            textAlign = TextAlign.Center,
            modifier = Modifier.fillMaxWidth(0.8f)
        )
        Spacer(modifier = Modifier.height(48.dp))

        if (isCompact) {
            Column(
                modifier = Modifier.fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(20.dp)
            ) {
                BentoCard(
                    icon = Icons.Default.Dashboard,
                    title = "Shared Dashboards",
                    desc = "One source of truth for your household. Unified visibility that ends the confusion of split expenses and separate accounts.",
                    accentColor = accentColor
                ) {
                    SharedDashboardsPreview()
                }
                BentoCard(
                    icon = Icons.Default.ShowChart,
                    title = "Budget Tracking",
                    desc = "Visual progress on your financial goals. Watch your savings grow with predictive analytics.",
                    accentColor = accentColor
                ) {
                    GoalProgressBars()
                }
                BentoCard(
                    icon = Icons.Default.AccountBalance,
                    title = "Bank Connectivity",
                    desc = "Connect to 10,000+ financial institutions worldwide through ultra-secure, encrypted API pipes.",
                    accentColor = accentColor
                ) {
                    BankConnectivityPreview()
                }
                BentoCard(
                    icon = Icons.Default.ShoppingCart,
                    title = "Recent Spending",
                    desc = "Analyze your spending habits with a beautiful 30-day trending graph and category-by-category breakdowns.",
                    accentColor = accentColor
                ) {
                    RecentSpendingPreview()
                }
            }
        } else {
            Column(
                modifier = Modifier.fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(24.dp)
            ) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(24.dp)
                ) {
                    BentoCard(
                        icon = Icons.Default.Dashboard,
                        title = "Shared Dashboards",
                        desc = "One source of truth for your household. Unified visibility that ends the confusion of split expenses and separate accounts.",
                        accentColor = accentColor,
                        modifier = Modifier.weight(1.1f)
                    ) {
                        SharedDashboardsPreview()
                    }
                    BentoCard(
                        icon = Icons.Default.ShowChart,
                        title = "Budget Tracking",
                        desc = "Visual progress on your financial goals. Watch your savings grow with predictive analytics.",
                        accentColor = accentColor,
                        modifier = Modifier.weight(1.0f)
                    ) {
                        GoalProgressBars()
                    }
                }
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(24.dp)
                ) {
                    BentoCard(
                        icon = Icons.Default.AccountBalance,
                        title = "Bank Connectivity",
                        desc = "Connect to 10,000+ financial institutions worldwide through ultra-secure, encrypted API pipes.",
                        accentColor = accentColor,
                        modifier = Modifier.weight(1.0f)
                    ) {
                        BankConnectivityPreview()
                    }
                    BentoCard(
                        icon = Icons.Default.ShoppingCart,
                        title = "Recent Spending",
                        desc = "Analyze your spending habits with a beautiful 30-day trending graph and category-by-category breakdowns.",
                        accentColor = accentColor,
                        modifier = Modifier.weight(1.0f)
                    ) {
                        RecentSpendingPreview()
                    }
                }
            }
        }
    }
}

@Composable
private fun BentoCard(
    icon: ImageVector,
    title: String,
    desc: String,
    accentColor: Color,
    modifier: Modifier = Modifier,
    extraContent: @Composable (ColumnScope.() -> Unit)? = null
) {
    Card(
        modifier = modifier
            .border(
                width = 1.dp,
                color = Color(0xFF1E2825),
                shape = RoundedCornerShape(16.dp)
            ),
        shape = RoundedCornerShape(16.dp),
        colors = CardDefaults.cardColors(
            containerColor = Color(0xFF131917)
        )
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(24.dp)
        ) {
            Box(
                modifier = Modifier
                    .size(48.dp)
                    .clip(RoundedCornerShape(8.dp))
                    .background(Color(0x1A57F1DB))
                    .border(1.dp, Color(0x3357F1DB), RoundedCornerShape(8.dp)),
                contentAlignment = Alignment.Center
            ) {
                Icon(
                    imageVector = icon,
                    contentDescription = null,
                    tint = accentColor,
                    modifier = Modifier.size(24.dp)
                )
            }
            
            Spacer(modifier = Modifier.height(20.dp))
            
            Text(
                text = title,
                style = MaterialTheme.typography.titleLarge.copy(
                    fontWeight = FontWeight.Bold,
                    color = Color.White
                )
            )
            Spacer(modifier = Modifier.height(8.dp))
            Text(
                text = desc,
                style = MaterialTheme.typography.bodyMedium.copy(
                    color = Color.Gray,
                    lineHeight = 22.sp
                )
            )
            
            if (extraContent != null) {
                Spacer(modifier = Modifier.height(20.dp))
                extraContent()
            }
        }
    }
}

@Composable
private fun GoalProgressBars() {
    Column(
        verticalArrangement = Arrangement.spacedBy(12.dp)
    ) {
        // Replica Budget Card 1 (Groceries)
        GlassCard(modifier = Modifier.fillMaxWidth()) {
            Column(modifier = Modifier.fillMaxWidth()) {
                Text(
                    text = "Groceries & Dining",
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.Bold,
                    color = Color.White
                )
                Spacer(modifier = Modifier.height(4.dp))
                Text(
                    text = "Food, Groceries, Restaurants, Fast Food",
                    style = MaterialTheme.typography.bodySmall,
                    color = Color.Gray
                )
                Spacer(modifier = Modifier.height(12.dp))
                LinearProgressIndicator(
                    progress = { 0.70f },
                    modifier = Modifier.fillMaxWidth().height(6.dp).clip(RoundedCornerShape(3.dp)),
                    color = Color(0xFF57F1DB),
                    trackColor = Color(0x33FFFFFF)
                )
                Spacer(modifier = Modifier.height(8.dp))
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Text(
                        text = "$420.50 spent of $600.00",
                        style = MaterialTheme.typography.bodySmall,
                        color = Color(0xFF57F1DB)
                    )
                    Text(
                        text = "-12.5% vs last mo.",
                        style = MaterialTheme.typography.labelSmall,
                        fontWeight = FontWeight.Bold,
                        color = Color(0xFF00C853)
                    )
                }
            }
        }
        // Replica Budget Card 2 (Entertainment)
        GlassCard(modifier = Modifier.fillMaxWidth()) {
            Column(modifier = Modifier.fillMaxWidth()) {
                Text(
                    text = "Entertainment",
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.Bold,
                    color = Color.White
                )
                Spacer(modifier = Modifier.height(4.dp))
                Text(
                    text = "Movies, Concerts, Gaming, Subscription Services",
                    style = MaterialTheme.typography.bodySmall,
                    color = Color.Gray
                )
                Spacer(modifier = Modifier.height(12.dp))
                LinearProgressIndicator(
                    progress = { 0.90f },
                    modifier = Modifier.fillMaxWidth().height(6.dp).clip(RoundedCornerShape(3.dp)),
                    color = Color(0xFFEF4444).copy(alpha = 0.7f),
                    trackColor = Color(0x33FFFFFF)
                )
                Spacer(modifier = Modifier.height(8.dp))
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Text(
                        text = "$180.00 spent of $200.00",
                        style = MaterialTheme.typography.bodySmall,
                        color = Color(0xFF57F1DB)
                    )
                    Text(
                        text = "+8.3% vs last mo.",
                        style = MaterialTheme.typography.labelSmall,
                        fontWeight = FontWeight.Bold,
                        color = Color(0xFFEF4444)
                    )
                }
            }
        }
    }
}

@Composable
private fun RecentSpendingGraph() {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .height(120.dp)
            .padding(vertical = 8.dp)
    ) {
        Canvas(modifier = Modifier.fillMaxSize()) {
            val width = size.width
            val height = size.height
            val paddingBottom = 16.dp.toPx()
            val graphHeight = height - paddingBottom

            val gridLines = 3
            for (i in 0..gridLines) {
                val y = graphHeight * (i.toFloat() / gridLines)
                drawLine(
                    color = Color(0x0DFFFFFF),
                    start = Offset(0f, y),
                    end = Offset(width, y),
                    strokeWidth = 1.dp.toPx()
                )
            }

            val points = listOf(
                Offset(0f, graphHeight * 0.8f),
                Offset(width * 0.2f, graphHeight * 0.7f),
                Offset(width * 0.4f, graphHeight * 0.85f),
                Offset(width * 0.6f, graphHeight * 0.4f),
                Offset(width * 0.8f, graphHeight * 0.6f),
                Offset(width, graphHeight * 0.3f)
            )

            val path = Path().apply {
                moveTo(points.first().x, points.first().y)
                for (i in 1 until points.size) {
                    val prev = points[i - 1]
                    val curr = points[i]
                    val cp1 = Offset(prev.x + (curr.x - prev.x) / 2f, prev.y)
                    val cp2 = Offset(prev.x + (curr.x - prev.x) / 2f, curr.y)
                    cubicTo(cp1.x, cp1.y, cp2.x, cp2.y, curr.x, curr.y)
                }
            }

            val fillPath = Path().apply {
                addPath(path)
                lineTo(width, height)
                lineTo(0f, height)
                close()
            }
            drawPath(
                path = fillPath,
                brush = Brush.verticalGradient(
                    colors = listOf(Color(0x3357F1DB), Color.Transparent),
                    startY = points.minOf { it.y },
                    endY = height
                )
            )

            drawPath(
                path = path,
                color = Color(0xFF57F1DB),
                style = Stroke(width = 3.dp.toPx(), cap = StrokeCap.Round)
            )
        }
    }
}

@Composable
private fun RecentSpendingPreview() {
    Column(
        verticalArrangement = Arrangement.spacedBy(10.dp)
    ) {
        RecentSpendingGraph()
        
        Spacer(modifier = Modifier.height(4.dp))
        
        // Category 1
        GlassCard(modifier = Modifier.fillMaxWidth()) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.SpaceBetween
            ) {
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    Box(
                        modifier = Modifier
                            .size(12.dp)
                            .background(Color(0xFF8B5CF6), CircleShape)
                    )
                    Column {
                        Text(
                            text = "Food & Drink",
                            style = MaterialTheme.typography.bodyLarge,
                            fontWeight = FontWeight.Bold,
                            color = Color.White
                        )
                        Text(
                            text = "42% of spending",
                            style = MaterialTheme.typography.labelSmall,
                            color = Color.Gray
                        )
                    }
                }
                Text(
                    text = "$420.50",
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.Bold,
                    color = Color.White
                )
            }
        }
        
        // Category 2
        GlassCard(modifier = Modifier.fillMaxWidth()) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.SpaceBetween
            ) {
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    Box(
                        modifier = Modifier
                            .size(12.dp)
                            .background(Color(0xFF06B6D4), CircleShape)
                    )
                    Column {
                        Text(
                            text = "Shopping",
                            style = MaterialTheme.typography.bodyLarge,
                            fontWeight = FontWeight.Bold,
                            color = Color.White
                        )
                        Text(
                            text = "28% of spending",
                            style = MaterialTheme.typography.labelSmall,
                            color = Color.Gray
                        )
                    }
                }
                Text(
                    text = "$280.00",
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.Bold,
                    color = Color.White
                )
            }
        }
    }
}

@Composable
private fun PricingSection(
    accentColor: Color,
    textSecondaryColor: Color,
    isCompact: Boolean,
    onNavigateToRegister: () -> Unit
) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .widthIn(max = 1200.dp),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Text(
            text = "Flexible Pricing for Every Household",
            style = MaterialTheme.typography.headlineMedium.copy(
                fontWeight = FontWeight.Bold,
                color = Color.White
            ),
            textAlign = TextAlign.Center
        )
        Spacer(modifier = Modifier.height(12.dp))
        Text(
            text = "Choose the plan that fits your family's needs. Upgrade or downgrade at any time.",
            style = MaterialTheme.typography.bodyLarge.copy(color = textSecondaryColor),
            textAlign = TextAlign.Center
        )
        Spacer(modifier = Modifier.height(48.dp))

        if (isCompact) {
            Column(
                modifier = Modifier.fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(24.dp)
            ) {
                PricingCard(
                    tier = "Free",
                    price = "$0",
                    features = listOf(
                        "Join Dashboards",
                        "Create Budgets",
                        "View Recent Spending",
                        "View Recurring Subscriptions"
                    ),
                    isHighlighted = false,
                    accentColor = accentColor,
                    onGetStarted = onNavigateToRegister
                )
                PricingCard(
                    tier = "Standard",
                    price = "$5",
                    features = listOf(
                        "Add up to 3 Financial Institutions",
                        "Create and Join Dashboards",
                        "Create Budgets",
                        "View Recent Spending",
                        "View Recurring Subscriptions"
                    ),
                    isHighlighted = false,
                    accentColor = accentColor,
                    onGetStarted = onNavigateToRegister
                )
                PricingCard(
                    tier = "Premium",
                    price = "$10",
                    features = listOf(
                        "Add up to 12 Financial Institutions",
                        "Add up to 4 Real Estate Holdings",
                        "Create and Join Dashboards",
                        "Create Budgets",
                        "View Recent Spending",
                        "View Recurring Subscriptions"
                    ),
                    isHighlighted = true,
                    accentColor = accentColor,
                    onGetStarted = onNavigateToRegister
                )
            }
        } else {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(IntrinsicSize.Max),
                horizontalArrangement = Arrangement.spacedBy(24.dp)
            ) {
                Box(modifier = Modifier.weight(1f).fillMaxHeight()) {
                    PricingCard(
                        tier = "Free",
                        price = "$0",
                        features = listOf(
                            "Join Dashboards",
                            "Create Budgets",
                            "View Recent Spending",
                            "View Recurring Subscriptions"
                        ),
                        isHighlighted = false,
                        accentColor = accentColor,
                        onGetStarted = onNavigateToRegister,
                        modifier = Modifier.fillMaxHeight()
                    )
                }
                Box(modifier = Modifier.weight(1f).fillMaxHeight()) {
                    PricingCard(
                        tier = "Standard",
                        price = "$5",
                        features = listOf(
                            "Add up to 3 Financial Institutions",
                            "Create and Join Dashboards",
                            "Create Budgets",
                            "View Recent Spending",
                            "View Recurring Subscriptions"
                        ),
                        isHighlighted = false,
                        accentColor = accentColor,
                        onGetStarted = onNavigateToRegister,
                        modifier = Modifier.fillMaxHeight()
                    )
                }
                Box(modifier = Modifier.weight(1f).fillMaxHeight()) {
                    PricingCard(
                        tier = "Premium",
                        price = "$10",
                        features = listOf(
                            "Add up to 12 Financial Institutions",
                            "Add up to 4 Real Estate Holdings",
                            "Create and Join Dashboards",
                            "Create Budgets",
                            "View Recent Spending",
                            "View Recurring Subscriptions"
                        ),
                        isHighlighted = true,
                        accentColor = accentColor,
                        onGetStarted = onNavigateToRegister,
                        modifier = Modifier.fillMaxHeight()
                    )
                }
            }
        }
    }
}

@Composable
private fun PricingCard(
    tier: String,
    price: String,
    features: List<String>,
    isHighlighted: Boolean,
    accentColor: Color,
    onGetStarted: () -> Unit,
    modifier: Modifier = Modifier
) {
    val highlightBorder = if (isHighlighted) Modifier.border(2.dp, accentColor, RoundedCornerShape(12.dp)) else Modifier
    
    GlassCard(
        modifier = modifier
            .fillMaxWidth()
            .then(highlightBorder)
    ) {
        Column(
            modifier = Modifier.fillMaxWidth().fillMaxHeight(),
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            Column {
                if (isHighlighted) {
                    Box(
                        modifier = Modifier
                            .background(accentColor.copy(alpha = 0.1f), RoundedCornerShape(50))
                            .border(1.dp, accentColor.copy(alpha = 0.3f), RoundedCornerShape(50))
                            .padding(horizontal = 12.dp, vertical = 4.dp)
                    ) {
                        Text(
                            text = "POPULAR",
                            style = MaterialTheme.typography.labelSmall,
                            color = accentColor,
                            fontWeight = FontWeight.Bold
                        )
                    }
                    Spacer(modifier = Modifier.height(8.dp))
                }
                Text(
                    text = tier,
                    style = MaterialTheme.typography.headlineSmall,
                    fontWeight = FontWeight.Bold,
                    color = Color.White
                )
                Spacer(modifier = Modifier.height(8.dp))
                Row(
                    verticalAlignment = Alignment.Bottom
                ) {
                    Text(
                        text = price,
                        style = MaterialTheme.typography.displayMedium,
                        fontWeight = FontWeight.Bold,
                        color = Color.White
                    )
                    Text(
                        text = "/month",
                        style = MaterialTheme.typography.bodyMedium,
                        color = Color.Gray,
                        modifier = Modifier.padding(bottom = 8.dp, start = 4.dp)
                    )
                }
            }

            Divider(color = Color(0x1AFFFFFF), thickness = 1.dp)

            Column(
                verticalArrangement = Arrangement.spacedBy(12.dp)
            ) {
                features.forEach { feature ->
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(12.dp)
                    ) {
                        Icon(
                            imageVector = Icons.Default.Check,
                            contentDescription = "Included",
                            tint = accentColor,
                            modifier = Modifier.size(20.dp)
                        )
                        Text(
                            text = feature,
                            style = MaterialTheme.typography.bodyMedium,
                            color = Color(0xFFBACAC5)
                        )
                    }
                }
            }

            Spacer(modifier = Modifier.weight(1f))

            val isPrimaryButton = isHighlighted || tier == "Standard"
            val buttonColors = if (isPrimaryButton) {
                ButtonDefaults.buttonColors(
                    containerColor = accentColor,
                    contentColor = Color(0xFF0E1513)
                )
            } else {
                ButtonDefaults.outlinedButtonColors(
                    contentColor = accentColor
                )
            }

            if (isPrimaryButton) {
                Button(
                    onClick = onGetStarted,
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(12.dp),
                    colors = buttonColors
                ) {
                    Text(
                        text = "Get Started",
                        style = MaterialTheme.typography.labelLarge,
                        fontWeight = FontWeight.Bold
                    )
                }
            } else {
                OutlinedButton(
                    onClick = onGetStarted,
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(12.dp),
                    colors = buttonColors,
                    border = BorderStroke(1.dp, accentColor.copy(alpha = 0.5f))
                ) {
                    Text(
                        text = "Get Started",
                        style = MaterialTheme.typography.labelLarge,
                        fontWeight = FontWeight.Bold
                    )
                }
            }
        }
    }
}

@Composable
private fun FaqSection(
    accentColor: Color,
    textSecondaryColor: Color
) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .widthIn(max = 800.dp),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Text(
            text = "Frequently Asked Questions",
            style = MaterialTheme.typography.headlineMedium.copy(
                fontWeight = FontWeight.Bold,
                color = Color.White
            ),
            textAlign = TextAlign.Center
        )
        Spacer(modifier = Modifier.height(12.dp))
        Text(
            text = "Everything you need to know about setting up your household on Financier.",
            style = MaterialTheme.typography.bodyLarge.copy(color = textSecondaryColor),
            textAlign = TextAlign.Center
        )
        Spacer(modifier = Modifier.height(40.dp))

        Column(
            modifier = Modifier.fillMaxWidth(),
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            FaqItem(
                question = "Is my financial data secure?",
                answer = "Absolutely. We use bank-grade 256-bit AES encryption for all data at rest and TLS 1.2+ for data in transit. We will NEVER sell your data, and we don't store your bank login credentials—we use secure tokens through our partners like Plaid.",
                accentColor = accentColor
            )
            FaqItem(
                question = "Why do you charge a monthly fee?",
                answer = "Our philosophy revolves around one concept, we will NEVER sell your data. The only way we make money is from you paying us. There is a concept in this day and age, if you are not paying for a product, YOU are the product. We do not use advertisements, we do not contribute to targeted ads platforms or use cookie tracking, and we do not sell generalized demographics data.",
                accentColor = accentColor
            )
            FaqItem(
                question = "Why have shared dashboards?",
                answer = "Every relationship is unique. From roommates, couples just starting to navigate joining their financial life, to long term couples that prefer to keep certain accounts separate, or parents trying to teach their children fiscal responsibility. A shared dashboard allows everyone to have a full picture of their financial health without having to compromise on security.",
                accentColor = accentColor
            )
            FaqItem(
                question = "How many dashboard members can I add?",
                answer = "Your dashboards can have an unlimited amount of members, with individual profiles and custom permission sets, allowing everyone to contribute to the financial picture without compromising privacy where needed.",
                accentColor = accentColor
            )
            FaqItem(
                question = "Who controls my data if I add an account to a dashboard?",
                answer = "You. Plain and simple. You can remove your account from a shared dashboard at any time, and none of your financial data will persist for the other members. A dashboard owner can remove your account from the dashboard, but they do not have access to any of your financial institutions or login credentials.",
                accentColor = accentColor
            )
            FaqItem(
                question = "What's the cost?",
                answer = "Creating an account is free, so is joining any shared dashboard. You must have a subscription in order to integrate with your financial institutions, which is $10/month. There is no commitment, cancel anytime.",
                accentColor = accentColor
            )
            FaqItem(
                question = "What happens to my data if I cancel my subscription?",
                answer = "When you cancel your subscription we will automatically unlink any account you have integrate into your account. When an account is unlinked, either automatically by us or manually on the Manage Accounts page, all of the data associated to that account is permanently removed from our database. Any dashboards that used that data will lose access to it instantly because it no longer exists to us. We strive to maintain complete security and transparency around someones financial data, because like you that peace of mind means EVERYTHING to us.",
                accentColor = accentColor
            )
        }
    }
}

@Composable
private fun FaqItem(
    question: String,
    answer: String,
    accentColor: Color
) {
    var isExpanded by remember { mutableStateOf(false) }

    GlassCard(
        modifier = Modifier
            .fillMaxWidth()
            .animateContentSize()
            .clickable { isExpanded = !isExpanded }
    ) {
        Column(
            modifier = Modifier.padding(8.dp)
        ) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Text(
                    text = question,
                    style = MaterialTheme.typography.bodyLarge.copy(
                        fontWeight = FontWeight.Bold,
                        color = Color.White
                    ),
                    modifier = Modifier.weight(1f)
                )
                Icon(
                    imageVector = if (isExpanded) Icons.Default.ArrowDropUp else Icons.Default.ArrowDropDown,
                    contentDescription = if (isExpanded) "Collapse" else "Expand",
                    tint = accentColor,
                    modifier = Modifier.size(24.dp)
                )
            }
            
            if (isExpanded) {
                Spacer(modifier = Modifier.height(12.dp))
                Text(
                    text = answer,
                    style = MaterialTheme.typography.bodyMedium.copy(
                        color = Color.Gray,
                        lineHeight = 22.sp
                    )
                )
            }
        }
    }
}

@Composable
private fun CtaSection(
    accentGradient: Brush,
    accentColor: Color,
    textSecondaryColor: Color,
    onGetStarted: () -> Unit
) {
    GlassCard(
        modifier = Modifier
            .fillMaxWidth()
            .widthIn(max = 1200.dp)
            .clip(RoundedCornerShape(32.dp))
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(vertical = 64.dp, horizontal = 24.dp),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Text(
                text = "Ready to Take Control of Your Financial Life?",
                style = MaterialTheme.typography.headlineLarge.copy(
                    fontWeight = FontWeight.Black,
                    color = Color.White
                ),
                textAlign = TextAlign.Center
            )
            Spacer(modifier = Modifier.height(16.dp))
            Text(
                text = "Join thousands of roommates, friends, and families who have found financial peace of mind through collaboration and clarity.",
                style = MaterialTheme.typography.bodyLarge.copy(color = textSecondaryColor),
                textAlign = TextAlign.Center,
                modifier = Modifier.fillMaxWidth(0.8f)
            )
            Spacer(modifier = Modifier.height(40.dp))

            Button(
                onClick = {}, //onGetStarted,
                colors = ButtonDefaults.buttonColors(containerColor = Color.Transparent),
                contentPadding = PaddingValues(),
                shape = RoundedCornerShape(16.dp),
                modifier = Modifier
                    .height(56.dp)
                    .width(260.dp)
                    .background(accentGradient, RoundedCornerShape(16.dp))
            ) {
                Text(
                    text = "Get Started Now",
                    style = MaterialTheme.typography.titleMedium.copy(
                        fontWeight = FontWeight.Bold,
                        color = Color(0xFF0E1513)
                    )
                )
            }
        }
    }
}

@Composable
private fun FooterSection(
    primaryColor: Color,
    textSecondaryColor: Color,
    onPrivacyClick: () -> Unit = {},
    onTermsClick: () -> Unit = {}
) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .widthIn(max = 1200.dp)
            .padding(top = 40.dp, bottom = 24.dp)
    ) {
        Divider(color = Color(0x1AFFFFFF), thickness = 1.dp)
        Spacer(modifier = Modifier.height(40.dp))
        
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.Top
        ) {
            Column(
                modifier = Modifier.weight(1.5f)
            ) {
                Text(
                    text = "Our Daily Finances",
                    style = MaterialTheme.typography.titleLarge.copy(
                        fontWeight = FontWeight.Bold,
                        color = primaryColor
                    )
                )
                Spacer(modifier = Modifier.height(12.dp))
                Text(
                    text = "The premium financial hub for modern households.",
                    style = MaterialTheme.typography.bodyMedium.copy(color = textSecondaryColor)
                )
            }
            
            Column(
                modifier = Modifier.weight(1f),
                horizontalAlignment = Alignment.Start
            ) {
                Text("Product", color = primaryColor, fontWeight = FontWeight.Bold)
                Spacer(modifier = Modifier.height(8.dp))
                Text("Features", color = textSecondaryColor, modifier = Modifier.clickable { })
                Text("Shared Dashboards", color = textSecondaryColor, modifier = Modifier.clickable { })
                Text("API Docs", color = textSecondaryColor, modifier = Modifier.clickable { })
            }
            
            Column(
                modifier = Modifier.weight(1f),
                horizontalAlignment = Alignment.Start
            ) {
                Text("Legal", color = primaryColor, fontWeight = FontWeight.Bold)
                Spacer(modifier = Modifier.height(8.dp))
                Text("Privacy Policy", color = textSecondaryColor, modifier = Modifier.clickable { onPrivacyClick() })
                Text("Terms of Service", color = textSecondaryColor, modifier = Modifier.clickable { onTermsClick() })
            }
        }
        
        Spacer(modifier = Modifier.height(48.dp))
        Divider(color = Color(0x0DFFFFFF), thickness = 1.dp)
        Spacer(modifier = Modifier.height(24.dp))
        
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            Text(
                text = "© 2026 Our Daily Finances Inc. All rights reserved.",
                style = MaterialTheme.typography.bodySmall.copy(color = textSecondaryColor)
            )
        }
    }
}

@Composable
private fun IntegrationReadyRow(
    textSecondaryColor: Color,
    alignment: Alignment.Horizontal
) {
    Column(
        modifier = Modifier
            .padding(top = 32.dp)
            .graphicsLayer(alpha = 0.6f),
        horizontalAlignment = alignment
    ) {
        Text(
            text = "INTEGRATION READY:",
            style = MaterialTheme.typography.labelSmall.copy(
                fontWeight = FontWeight.Bold,
                color = textSecondaryColor,
                letterSpacing = 1.sp
            )
        )
        Spacer(modifier = Modifier.height(8.dp))
        Row(
            horizontalArrangement = Arrangement.spacedBy(16.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Icon(
                imageVector = Icons.Default.AccountBalance,
                contentDescription = "Bank Integration",
                tint = Color.White,
                modifier = Modifier.size(24.dp)
            )
            Icon(
                imageVector = Icons.Default.CreditCard,
                contentDescription = "Card Integration",
                tint = Color.White,
                modifier = Modifier.size(24.dp)
            )
            Icon(
                imageVector = Icons.Default.Payments,
                contentDescription = "Payments Integration",
                tint = Color.White,
                modifier = Modifier.size(24.dp)
            )
        }
    }
}

@Composable
private fun SharedDashboardsPreview() {
    Column(
        verticalArrangement = Arrangement.spacedBy(12.dp)
    ) {
        // Replica 1
        GlassCard(modifier = Modifier.fillMaxWidth()) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.SpaceBetween
            ) {
                Row(
                    modifier = Modifier.weight(1f),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Column(modifier = Modifier.weight(1f)) {
                        Text(
                            text = "Family Finances",
                            style = MaterialTheme.typography.titleMedium,
                            fontWeight = FontWeight.Bold,
                            color = Color.White
                        )
                    }
                    Spacer(modifier = Modifier.width(16.dp))
                    Text(
                        text = "$12,450.80",
                        style = MaterialTheme.typography.titleMedium,
                        fontWeight = FontWeight.Bold,
                        color = Color(0xFF57F1DB)
                    )
                }
                Spacer(modifier = Modifier.width(8.dp))
                Icon(
                    imageVector = Icons.Default.ChevronRight,
                    contentDescription = "View Details",
                    tint = Color.Gray,
                    modifier = Modifier.size(20.dp)
                )
            }
        }
        // Replica 2
        GlassCard(modifier = Modifier.fillMaxWidth()) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.SpaceBetween
            ) {
                Row(
                    modifier = Modifier.weight(1f),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Column(modifier = Modifier.weight(1f)) {
                        Text(
                            text = "Vacation Fund 2026",
                            style = MaterialTheme.typography.titleMedium,
                            fontWeight = FontWeight.Bold,
                            color = Color.White
                        )
                    }
                    Spacer(modifier = Modifier.width(16.dp))
                    Text(
                        text = "$4,200.00",
                        style = MaterialTheme.typography.titleMedium,
                        fontWeight = FontWeight.Bold,
                        color = Color(0xFF57F1DB)
                    )
                }
                Spacer(modifier = Modifier.width(8.dp))
                Icon(
                    imageVector = Icons.Default.ChevronRight,
                    contentDescription = "View Details",
                    tint = Color.Gray,
                    modifier = Modifier.size(20.dp)
                )
            }
        }
    }
}

@Composable
private fun BankConnectivityPreview() {
    Column(
        verticalArrangement = Arrangement.spacedBy(12.dp)
    ) {
        // Replica Account Card 1 (Chase checking)
        GlassCard(modifier = Modifier.fillMaxWidth()) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Column(modifier = Modifier.weight(1f)) {
                    Text(
                        text = "Chase Checking",
                        style = MaterialTheme.typography.headlineMedium,
                        color = Color.White
                    )
                    Spacer(modifier = Modifier.height(2.dp))
                    Text(
                        text = "Chase Bank",
                        style = MaterialTheme.typography.bodyMedium,
                        color = Color.Gray
                    )
                    Spacer(modifier = Modifier.height(4.dp))
                    Box(
                        modifier = Modifier
                            .background(
                                Color(0x1FBEC6E0),
                                shape = RoundedCornerShape(999.dp)
                            )
                            .padding(horizontal = 8.dp, vertical = 2.dp)
                    ) {
                        Text(
                            text = "CHECKING",
                            style = MaterialTheme.typography.labelSmall,
                            color = Color(0xFFBEC6E0)
                        )
                    }
                }
                Spacer(modifier = Modifier.width(8.dp))
                Text(
                    text = "$3,450.20",
                    style = MaterialTheme.typography.bodyMedium,
                    color = Color.White
                )
            }
        }
        // Replica Account Card 2 (Amex Gold)
        GlassCard(modifier = Modifier.fillMaxWidth()) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Column(modifier = Modifier.weight(1f)) {
                    Text(
                        text = "Savings",
                        style = MaterialTheme.typography.headlineMedium,
                        color = Color.White
                    )
                    Spacer(modifier = Modifier.height(2.dp))
                    Text(
                        text = "Bank of America",
                        style = MaterialTheme.typography.bodyMedium,
                        color = Color.Gray
                    )
                    Spacer(modifier = Modifier.height(4.dp))
                    Box(
                        modifier = Modifier
                            .background(
                                Color(0x1FBEC6E0),
                                shape = RoundedCornerShape(999.dp)
                            )
                            .padding(horizontal = 8.dp, vertical = 2.dp)
                    ) {
                        Text(
                            text = "SAVINGS",
                            style = MaterialTheme.typography.labelSmall,
                            color = Color(0xFFBEC6E0)
                        )
                    }
                }
                Spacer(modifier = Modifier.width(8.dp))
                Text(
                    text = "$850.40",
                    style = MaterialTheme.typography.bodyMedium,
                    color = Color.White
                )
            }
        }
    }
}
